/**
 * Hook for report management service operations
 */

import { useState, useEffect } from 'react';
import { ReportManagementService } from '@/services/reportManagement';
import { useAuth } from '@/hooks/useAuth';
import type { SavedReport } from '@/types/reports';

const reportService = new ReportManagementService();

export const useReportManagementService = () => {
  const { isAuthenticated, isApproved, loading: authLoading } = useAuth();
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getReports();
      setReports(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load data when authentication is complete and user is approved
    if (!authLoading && isAuthenticated && isApproved) {
      loadReports();
    } else if (!authLoading && (!isAuthenticated || !isApproved)) {
      // Reset state if user is not authenticated or approved
      setReports([]);
      setLoading(false);
      setError(null);
    }
  }, [authLoading, isAuthenticated, isApproved]);

  return {
    reports,
    loading,
    error,
    refetch: loadReports,
  };
};
